"use client";

import { useLocaleTag } from "@/lib/i18n/format";
import { IconOrEmoji } from "@v1/ui/icon-or-emoji";
import { cn } from "@v1/ui/utils";
import { ArrowLeft } from "lucide-react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { type ReactElement, useMemo } from "react";
import { HelpArticleJsonLd } from "./help-article-json-ld";
import { HelpBreadcrumb } from "./help-breadcrumb";
import { type HelpChildPage, HelpChildPageList } from "./help-child-page-list";
import {
  HELP_CHILD_LIST_ANCHOR,
  HELP_TOC_MIN_ITEMS,
  type HelpTocItem,
  extractHelpHeadings,
  helpTocItems,
} from "./help-headings";
import { useHelpHref } from "./help-link-base";
import { HelpMarkdown } from "./help-markdown";
import { HelpToc } from "./help-toc";

export type HelpArticleViewModel = {
  readonly organization_name: string;
  readonly org_slug: string;
  readonly space: {
    readonly space_id: string;
    readonly name: string;
  };
  readonly page: {
    readonly page_id: string;
    readonly title: string;
    readonly icon: string | null;
    readonly description: string;
    readonly last_edited_at: number;
  };
  /** Served ancestors root → parent. */
  readonly ancestors: readonly {
    readonly page_id: string;
    readonly title: string;
  }[];
  readonly children: readonly HelpChildPage[];
  readonly content_md: string;
  readonly content_truncated: boolean;
  readonly link_targets: Readonly<Record<string, { readonly title: string }>>;
};

export function bodyWithoutLeadingTitle(contentMd: string, title: string): string {
  const trimmed = contentMd.replace(/^\s+/, "");
  const newlineIndex = trimmed.indexOf("\n");
  const firstLine =
    newlineIndex >= 0 ? trimmed.slice(0, newlineIndex) : trimmed;
  const rest = newlineIndex >= 0 ? trimmed.slice(newlineIndex + 1) : "";
  const firstLineText = firstLine.replace(/^#{1,6}\s*/, "").trim();
  if (firstLine.startsWith("# ") || firstLineText === title.trim()) return rest;
  return contentMd;
}

/** Presentational public help article (SSR + client SPA share this). */
export function HelpArticleView({
  result,
  includeJsonLd,
  embedded = false,
}: {
  readonly result: HelpArticleViewModel;
  readonly includeJsonLd: boolean;
  /** Inside the portal panel: no sticky header above, less width. */
  readonly embedded?: boolean;
}): ReactElement {
  const localeTag = useLocaleTag();
  const t = useTranslations("public.helpArticleView");
  const orgSlug = result.org_slug;
  const helpHref = useHelpHref(orgSlug);
  const body = bodyWithoutLeadingTitle(result.content_md, result.page.title);
  const hasBody = body.trim().length > 0;
  const hasChildren = result.children.length > 0;
  const childListLabel = t("artikel");

  const headings = useMemo(
    () =>
      extractHelpHeadings(body, hasChildren ? [HELP_CHILD_LIST_ANCHOR] : []),
    [body, hasChildren],
  );
  const tocItems = useMemo<HelpTocItem[]>(() => {
    const items = helpTocItems(headings);
    if (!hasChildren || !hasBody) return items;
    return [
      ...items,
      { id: HELP_CHILD_LIST_ANCHOR, text: childListLabel, level: 1 },
    ];
  }, [headings, hasChildren, hasBody, childListLabel]);

  const updatedAt = new Intl.DateTimeFormat(localeTag, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(new Date(result.page.last_edited_at));

  const crumbs = [
    {
      label: result.space.name,
      href: helpHref({
        kind: "space",
        spaceId: result.space.space_id,
      }),
    },
    ...result.ancestors.map((ancestor) => ({
      label: ancestor.title,
      href: helpHref({ kind: "page", pageId: ancestor.page_id }),
    })),
  ];

  return (
    <div className="mx-auto flex w-full max-w-[936px] items-start gap-10">
      <article className="min-w-0 max-w-[680px] flex-1">
        {includeJsonLd ? (
          <HelpArticleJsonLd
            org_slug={orgSlug}
            organization_name={result.organization_name}
            all_topics_label={t("alleThemen")}
            space={result.space}
            ancestors={result.ancestors}
            page={result.page}
          />
        ) : null}
        <HelpBreadcrumb
          org_slug={orgSlug}
          items={crumbs}
          current={result.page.title}
        />

        <header>
          <h1 className="text-[26px] font-bold leading-tight tracking-tight text-primary">
            {result.page.icon ? (
              <span className="mr-2 inline-flex align-middle">
                <IconOrEmoji value={result.page.icon} fallback="" size={22} />
              </span>
            ) : null}
            {result.page.title}
          </h1>
          {hasChildren ? (
            <p className="mt-2.5 text-[12.5px] text-primary/45">
              {t("seiten", { count: result.children.length })}
            </p>
          ) : null}
        </header>

        {hasBody ? (
          <div className="mt-5">
            <HelpMarkdown
              content_md={body}
              org_slug={orgSlug}
              link_targets={result.link_targets}
              headings={headings}
            />
            {result.content_truncated ? (
              <p className="mt-4 text-[12px] text-primary/45">
                {t("dieseSeiteWurdeGekuerzt")}
              </p>
            ) : null}
          </div>
        ) : null}

        {hasChildren ? (
          <section className={cn(hasBody ? "mt-10" : "mt-6")}>
            {hasBody ? (
              <h2
                id={HELP_CHILD_LIST_ANCHOR}
                className="mb-2 scroll-mt-[81px] text-[17px] font-semibold text-primary/90"
              >
                {childListLabel}
              </h2>
            ) : null}
            <HelpChildPageList org_slug={orgSlug} pages={result.children} />
          </section>
        ) : null}

        <footer className="mt-10 flex items-center justify-between gap-3 border-t border-border/50 pt-4">
          <Link
            href={helpHref({ kind: "center" })}
            prefetch={false}
            className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-primary/60 transition-colors hover:text-primary"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {t("alleThemen")}
          </Link>
          <span className="text-[11.5px] text-primary/40">
            {t("zuletztAktualisiert", { date: updatedAt })}
          </span>
        </footer>
      </article>

      {tocItems.length >= HELP_TOC_MIN_ITEMS ? (
        <aside
          className={cn(
            "sticky hidden w-[208px] shrink-0 overflow-y-auto overscroll-contain [scrollbar-gutter:stable]",
            embedded
              ? "top-10 max-h-[calc(100vh-80px)] 2xl:block"
              : "top-[105px] max-h-[calc(100vh-130px)] xl:block",
          )}
        >
          <HelpToc key={result.page.page_id} items={tocItems} />
        </aside>
      ) : null}
    </div>
  );
}
